import React from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Box, MenuItem, Select } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import styled from "styled-components";
import useTheme from "../../../common/hooks/useTheme";
import { Theme } from "../../../providers/ThemeProvider";
import SecondaryTabMenuPicker from "../components/common/SecondaryTabMenuPicker";
import PrimaryButton from "../../../common/ui/PrimaryButton";
import SecondaryButton from "../../../common/ui/SecondaryButton";
import PageLoader from "../../../common/ui/PageLoader";
import {
  ProfileFormProvider,
  ProfileTabKey,
  useProfileForm,
} from "../components/profile/ProfileFormContext";
import { useSubject } from "../providers/SubjectProvider";
import { isProfileIndexPath, profileTabPath } from "../utils/profileTabPath";

/**
 * The Profile screen — the client's own record, split across three tabs.
 *
 * Replaces the old Client Info → Biometrics / Nutrition tabs, which are gone
 * from that tree. Tab state lives in the URL (one child route each) so tabs are
 * deep-linkable and the dirty tab is addressable, unlike the previous Nutrition
 * inner tabs which used local state.
 */

const TABS: { label: string; key: number; segment: ProfileTabKey }[] = [
  { label: "Basic information", key: 0, segment: "basic-information" },
  { label: "Onboarding", key: 1, segment: "onboarding" },
  {
    label: "Nutrition Questionnaire",
    key: 2,
    segment: "nutrition-questionnaire",
  },
];

// Padding of WorkspaceShell's content box ("24px 16px"). The save bar pins
// itself past it on all three sides — see styles.saveBar.
const SHELL_CONTENT_PADDING_BOTTOM = 24;
const SHELL_CONTENT_PADDING_X = 16;

// Height of the sidebar's account footer (12px padding + the 42px avatar row +
// its 1px divider). The save bar sits directly beside it along the bottom edge,
// so the two top borders have to be the same line — see styles.saveBar.
const SIDEBAR_FOOTER_HEIGHT = 67;

const StyledSelect = styled(Select)(({ theme }) => ({
  height: "36px",
  width: "183px",
  borderRadius: "8px",
  backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.colors_ds.NEUTRAL_BORDER_WEAK,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.colors_ds.PRIMARY_ICON_MAIN,
  },
  legend: { display: "none" },
  fieldset: { top: 0 },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  margin: "4px",
  borderRadius: "8px",
  display: "flex",
  fontFamily: "var(--font-Medium)",
  fontSize: "12px",
  color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  justifyContent: "space-between",
  "&:hover": { backgroundColor: theme.colorTokens.N[40] },
  "&.Mui-selected": { backgroundColor: theme.colorTokens.P[10] },
  "&.Mui-selected:hover": { backgroundColor: theme.colorTokens.P[10] },
}));

const ProfileScreenContent = () => {
  const theme: Theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    massUnit,
    onUnitChange,
    isBasicInformationDirty,
    isOnboardingDirty,
    dirtyTabs,
    isSendingData,
    isLoadingQuestionnaire,
    handleSave,
    handleCancel,
  } = useProfileForm();

  const activeIndex = Math.max(
    0,
    TABS.findIndex((tab) => location.pathname.endsWith(`/${tab.segment}`))
  );

  const onTabChange = (index: number) => {
    const target = TABS[index];
    if (!target) return;
    // Anchored on the `/profile` segment (not "strip the last segment") so it
    // also works from the bare `/…/profile` index URL, preserving the query
    // string (?id=…, chat mode).
    navigate(
      `${profileTabPath(location.pathname, target.segment)}${location.search}`
    );
  };

  // Onboarding only — it owns every field the unit applies to (weights,
  // height). Basic information has none, so the picker there was just a control
  // with nothing to act on.
  const showUnitSelector = TABS[activeIndex]?.segment === "onboarding";

  // Nutrition Questionnaire is read-only, so Save/Cancel would act on edits made
  // somewhere the user cannot see. The tab dots still mark where those edits are.
  const showSaveBar =
    dirtyTabs.length > 0 &&
    TABS[activeIndex]?.segment !== "nutrition-questionnaire";

  // "Basic information", or "Basic information and Onboarding" when both hold
  // edits — one bar, so a single Save/Cancel visibly covers everything rather
  // than naming whichever tab happened to be edited first.
  const dirtyLabels = dirtyTabs
    .map((segment) => TABS.find((tab) => tab.segment === segment)?.label)
    .filter(Boolean);
  const dirtyLabel =
    dirtyLabels.length > 1
      ? `${dirtyLabels.slice(0, -1).join(", ")} and ${
          dirtyLabels[dirtyLabels.length - 1]
        }`
      : dirtyLabels[0];

  if (isSendingData) {
    return <PageLoader />;
  }

  return (
    <Box sx={styles.container}>
      <Box sx={styles.tabRow()}>
        <SecondaryTabMenuPicker
          tabs={TABS.map((tab) => ({
            label: tab.label,
            key: tab.key,
            isDirty:
              (tab.segment === "basic-information" && isBasicInformationDirty) ||
              (tab.segment === "onboarding" && isOnboardingDirty),
          }))}
          selectedTab={activeIndex}
          setSelectedTab={onTabChange}
          isFullWidth={false}
        />
        {showUnitSelector && (
          <StyledSelect
            id="measurement-unit-select"
            value={massUnit}
            onChange={onUnitChange}
            theme={theme}
            IconComponent={KeyboardArrowDownIcon}
            MenuProps={{
              PaperProps: {
                sx: {
                  marginTop: "8px",
                  backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
                  boxShadow: "var(--box-shadow-general)",
                  borderRadius: "8px",
                  border: `1px solid ${theme.colors_ds.PRIMARY_ICON_MAIN}`,
                },
              },
            }}
          >
            <StyledMenuItem value={1} theme={theme}>
              Metric (kg, cm)
            </StyledMenuItem>
            <StyledMenuItem value={2} theme={theme}>
              Imperial (lbs, ft)
            </StyledMenuItem>
          </StyledSelect>
        )}
      </Box>

      <Box sx={styles.body(showSaveBar)}>
        {/* The tab strip stays interactive while the questionnaire loads; only
            the fields below it wait, since they would otherwise render empty. */}
        {/* The index route is only a redirect to the first tab; let it run
            while loading so the URL settles before anything is clickable. */}
        {isLoadingQuestionnaire && !isProfileIndexPath(location.pathname) ? (
          <PageLoader />
        ) : (
          <Outlet />
        )}
      </Box>

      {/* Sticky save bar — appears only while a tab holds unsaved changes, and
          names which tab, so edits are never lost by switching tabs. */}
      {showSaveBar && (
        <Box sx={styles.saveBar(theme)}>
          <Box sx={styles.saveBarMessage(theme)}>
            <Box sx={styles.dirtyDot(theme)} />
            {`Unsaved changes in ${dirtyLabel}`}
          </Box>
          <Box sx={styles.saveBarActions}>
            <SecondaryButton label="Cancel" onClick={handleCancel} />
            <PrimaryButton label="Save" onClick={handleSave} />
          </Box>
        </Box>
      )}
    </Box>
  );
};

const ProfileScreen = () => {
  const { status } = useSubject();

  // WorkspaceShell already gates on subject status, but Profile reads the
  // questionnaire slices directly — bail out rather than render empty fields.
  if (status !== "ready") {
    return null;
  }

  return (
    <ProfileFormProvider>
      <ProfileScreenContent />
    </ProfileFormProvider>
  );
};

export default ProfileScreen;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as const,
    flex: 1,
    width: "100%",
  },
  tabRow: () => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "24px",
    marginBottom: "16px",
  }),
  // Leave room for the sticky bar so the last card is never covered by it.
  body: (hasSaveBar: boolean) => ({
    flex: 1,
    // Bar height plus the padding it now eats into, so the last card clears it.
    paddingBottom: hasSaveBar ? `${72 + SHELL_CONTENT_PADDING_BOTTOM}px` : 0,
  }),
  saveBar: (theme: Theme) => ({
    position: "sticky" as const,
    bottom: 0,
    // The shell pads its content box by 24px, which is where the bar would come
    // to rest once the page is scrolled to the end — 24px below where sticky
    // holds it the rest of the time, so the bar appeared to shift and leave a
    // strip of background under itself. Cancelling that padding makes the
    // resting position and the stuck position the same, on short pages
    // (Onboarding) and long ones (Nutrition) alike.
    marginBottom: `-${SHELL_CONTENT_PADDING_BOTTOM}px`,
    // Same reasoning sideways: the bar is a full-bleed surface, so it runs to
    // the edges of the content area rather than sitting inside the shell's
    // gutter. The gutter is added back as padding below, which keeps the label
    // and the buttons on the same vertical lines as the fields above them.
    marginLeft: `-${SHELL_CONTENT_PADDING_X}px`,
    marginRight: `-${SHELL_CONTENT_PADDING_X}px`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    // Matched to the sidebar footer beside it so the two dividers read as one
    // continuous line across the bottom of the screen.
    minHeight: `${SIDEBAR_FOOTER_HEIGHT}px`,
    boxSizing: "border-box" as const,
    padding: `12px ${16 + SHELL_CONTENT_PADDING_X}px`,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    borderTop: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    boxShadow: "var(--box-shadow-general)",
  }),
  saveBarMessage: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  dirtyDot: (theme: Theme) => ({
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    flexShrink: 0,
    backgroundColor: theme.colorTokens.W[60],
  }),
  saveBarActions: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
};
